import React from 'react';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';

interface BlogPreviewData {
  title?: string;
  subtitle?: string;
  maxPosts?: number;
  showExcerpt?: boolean;
  showDate?: boolean;
  showFeaturedImage?: boolean;
  linkText?: string;
  customColors?: {
    background?: string;
    title?: string;
    subtitle?: string;
    cardBackground?: string;
    cardTitle?: string;
    cardExcerpt?: string;
    linkColor?: string;
  };
}

interface BlogPreviewEditorProps {
  formData: BlogPreviewData;
  setFormData: (data: BlogPreviewData) => void;
}

export const BlogPreviewEditor: React.FC<BlogPreviewEditorProps> = ({ formData, setFormData }) => {
  const data = formData;
  const themeColors = useCustomColors();

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  const colorFields = getColorFieldsForComponent('blog-preview', themeColors);

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
        <input
          type="text"
          value={data.title || ''}
          onChange={e => setFormData({ ...data, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="From Our Blog"
          maxLength={200}
        />
      </div>

      {/* Subtitle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
        <input
          type="text"
          value={data.subtitle || ''}
          onChange={e => setFormData({ ...data, subtitle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Latest insights, updates, and stories"
          maxLength={500}
        />
      </div>

      {/* Display Options */}
      <div className="border-t pt-6">
        <h3 className="text-md font-medium text-gray-900 mb-4">Display Options</h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Number of posts</label>
            <select
              value={data.maxPosts ?? 3}
              onChange={e => setFormData({ ...data, maxPosts: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              {[1, 2, 3, 4, 5, 6].map(n => (
                <option key={n} value={n}>
                  {n} post{n !== 1 ? 's' : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Link text</label>
            <input
              type="text"
              value={data.linkText || ''}
              onChange={e => setFormData({ ...data, linkText: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Read more"
              maxLength={50}
            />
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.showExcerpt ?? true}
              onChange={e => setFormData({ ...data, showExcerpt: e.target.checked })}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Show excerpt</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.showDate ?? true}
              onChange={e => setFormData({ ...data, showDate: e.target.checked })}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Show date</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.showFeaturedImage ?? true}
              onChange={e => setFormData({ ...data, showFeaturedImage: e.target.checked })}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Show featured image</span>
          </label>
        </div>
      </div>

      {/* Color customization */}
      {colorFields.length > 0 && (
        <ColorCustomizationPanel
          title="Color Customization"
          description="Customize section and card colors. Leave empty to use theme defaults."
          colorFields={colorFields}
          customColors={data.customColors as Record<string, string | undefined>}
          onUpdateColor={updateCustomColor}
        />
      )}
    </div>
  );
};
